import {
  type ActualizarCategoriaGastoInput,
  type ActualizarCategoriaInput,
  type ActualizarCompetidorInput,
  type ActualizarConfiguracionInput,
  type ActualizarContactoInput,
  type ActualizarCuotaManualInput,
  type ActualizarOrganismoInput,
  type ActualizarParametrosPredictorConfigInput,
  type ActualizarUsuarioEmpresaInput,
  type CalcularMontosInput,
  type CambiarEstadoLicitacionInput,
  type ConfiguracionEmpresaDto,
  type CrearCategoriaGastoInput,
  type CrearCategoriaInput,
  type CrearCompetidorInput,
  type CrearContactoInput,
  type CrearCuotaManualInput,
  type CrearGastoInput,
  type CrearLicitacionInput,
  type CrearOrganismoInput,
  type CrearProyectoInput,
  type CrearUsuarioEmpresaInput,
  type DesgloseMontosDto,
  type EditarProyectoSeguroInput,
  type FusionarCompetidoresInput,
  type ModalidadMonto,
  type ModoEstrategiaPredictor,
  type PagarGastoInput,
  type ParametrosPredictorDto,
  type RegistrarActaLicitacionInput,
  type RegistrarPagoRapidoInput,
  type SimulacionPlaygroundRespuesta,
  type SimuladorPlaygroundInput,
  type TestImpactoInput,
  type TestImpactoRespuesta,
  type UsuarioEmpresaDto,
  type CrearRemuneracionInput,
  type CargarNominaMesInput,
  type PagarRemuneracionInput,
  type AsignarBonoLicitacionInput,
  type RemuneracionItem,
} from '@lexicore/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import {
  claves,
  type Categoria,
  type ComparacionBacktest,
  type ContactoOrganismo,
  type Cuota,
  type Factura,
  type Gasto,
  type GastoProyecto,
  type Hito,
  type ItemCierre,
  type NotaBitacora,
  type Organismo,
  type ParametrosPredictor,
  type ProyectoDetalle,
  type ResultadoBacktest,
} from './consultas';

/**
 * Escrituras del sistema.
 *
 * Separadas de `consultas.ts` porque comparten una regla que conviene tener en
 * un solo lugar: **cualquier cambio dentro de un proyecto repercute fuera de
 * él**. Cargar un gasto mueve la ficha, el libro consolidado de Costos, los KPIs
 * del tablero y posiblemente las alertas. Invalidar las ramas completas es más
 * simple —y mucho más difícil de equivocar— que actualizar cada caché a mano, y
 * con este volumen de datos el costo de refrescar es despreciable.
 */

type RamaAfectada = 'gastos' | 'facturas' | 'catalogos';

function useMutacionDeProyecto<TVariables, TResultado>(
  ejecutar: (variables: TVariables) => Promise<TResultado>,
  ramas: RamaAfectada[] = [],
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ejecutar,
    onSuccess: () => {
      // Siempre: la ficha del proyecto, los indicadores y los avisos.
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
      void cliente.invalidateQueries({ queryKey: ['notificaciones'] });

      for (const rama of ramas) {
        void cliente.invalidateQueries({ queryKey: claves[rama] });
      }
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Gastos
// ─────────────────────────────────────────────────────────────

export const useCrearGastoEnProyecto = () =>
  useMutacionDeProyecto(
    (datos: Record<string, unknown>) => api<GastoProyecto>('/gastos', { method: 'POST', body: datos }),
    ['gastos'],
  );

export const useCrearGastoDirecto = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearGastoInput | Record<string, unknown>) =>
      api<Gasto>('/gastos', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useActualizarGasto = () =>
  useMutacionDeProyecto(
    ({ id, ...datos }: { id: string } & Record<string, unknown>) =>
      api<GastoProyecto>(`/gastos/${id}`, { method: 'PATCH', body: datos }),
    ['gastos'],
  );

export const useMarcarGastoPagado = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos?: PagarGastoInput }) =>
      api<{ mensaje: string; gasto: Gasto }>(`/gastos/${id}/pagar`, {
        method: 'PATCH',
        body: datos ?? {},
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useEliminarGasto = () =>
  useMutacionDeProyecto((id: string) => api<void>(`/gastos/${id}`, { method: 'DELETE' }), ['gastos']);

export const useEjecutarRecurrentesMasivo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ generados: number; totalNetoGenerado: number; mensaje: string }>('/gastos/recurrentes/ejecutar', {
        method: 'POST',
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Presupuesto de costos
// ─────────────────────────────────────────────────────────────

export const useDefinirPresupuesto = () =>
  useMutacionDeProyecto(
    ({
      proyectoId,
      lineas,
    }: {
      proyectoId: string;
      lineas: { categoriaGastoId: string; montoPlanificado: number }[];
    }) => api<ProyectoDetalle>(`/proyectos/${proyectoId}/presupuesto`, { method: 'POST', body: { lineas } }),
  );

// ─────────────────────────────────────────────────────────────
// Hitos
// ─────────────────────────────────────────────────────────────

export const useCrearHito = () =>
  useMutacionDeProyecto(({ proyectoId, ...datos }: { proyectoId: string } & Record<string, unknown>) =>
    api<Hito>(`/proyectos/${proyectoId}/hitos`, { method: 'POST', body: datos }),
  );

export const useActualizarHito = () =>
  useMutacionDeProyecto(({ id, ...datos }: { id: string } & Record<string, unknown>) =>
    api<Hito>(`/proyectos/hitos/${id}`, { method: 'PATCH', body: datos }),
  );

export const useAlternarHito = () =>
  useMutacionDeProyecto((id: string) => api<Hito>(`/proyectos/hitos/${id}/alternar`, { method: 'POST' }));

export const useEliminarHito = () =>
  useMutacionDeProyecto((id: string) => api<void>(`/proyectos/hitos/${id}`, { method: 'DELETE' }));

// ─────────────────────────────────────────────────────────────
// Bitácora
// ─────────────────────────────────────────────────────────────

export const useAgregarNota = () =>
  useMutacionDeProyecto(({ proyectoId, texto }: { proyectoId: string; texto: string }) =>
    api<NotaBitacora>(`/proyectos/${proyectoId}/notas`, { method: 'POST', body: { texto } }),
  );

export const useEliminarNota = () =>
  useMutacionDeProyecto((id: string) => api<void>(`/proyectos/notas/${id}`, { method: 'DELETE' }));

// ─────────────────────────────────────────────────────────────
// Checklist de cierre
// ─────────────────────────────────────────────────────────────

export const useAgregarItemCierre = () =>
  useMutacionDeProyecto(({ proyectoId, texto }: { proyectoId: string; texto: string }) =>
    api<ItemCierre>(`/proyectos/${proyectoId}/checklist`, { method: 'POST', body: { texto } }),
  );

export const useAlternarItemCierre = () =>
  useMutacionDeProyecto((id: string) =>
    api<ItemCierre>(`/proyectos/checklist/${id}/alternar`, { method: 'POST' }),
  );

export const useEliminarItemCierre = () =>
  useMutacionDeProyecto((id: string) => api<void>(`/proyectos/checklist/${id}`, { method: 'DELETE' }));

// ─────────────────────────────────────────────────────────────
// Proyecto: datos, cierre y cronograma
// ─────────────────────────────────────────────────────────────

export const useCrearProyecto = () =>
  useMutacionDeProyecto(
    (datos: CrearProyectoInput) =>
      api<ProyectoDetalle>('/proyectos', { method: 'POST', body: datos }),
    ['catalogos'],
  );

export const useActualizarProyecto = () =>
  useMutacionDeProyecto(
    ({ id, ...datos }: { id: string } & Partial<EditarProyectoSeguroInput>) =>
      api<ProyectoDetalle>(`/proyectos/${id}`, { method: 'PATCH', body: datos }),
    ['catalogos'],
  );

export const useEliminarProyecto = () =>
  useMutacionDeProyecto(
    (id: string) =>
      api<{ eliminada: boolean; mensaje: string }>(`/proyectos/${id}`, { method: 'DELETE' }),
    ['gastos', 'facturas', 'catalogos'],
  );

export const useCambiarEstadoProyecto = () =>
  useMutacionDeProyecto(
    ({ id, estado, motivo }: { id: string; estado: string; motivo?: string }) =>
      api<ProyectoDetalle>(`/proyectos/${id}/estado`, { method: 'PATCH', body: { estado, motivo } }),
  );

export const useCerrarProyecto = () =>
  useMutacionDeProyecto(
    ({
      proyectoId,
      ...datos
    }: {
      proyectoId: string;
      forzar: boolean;
      notaCierre?: string;
      fechaFinReal?: string;
    }) => api<ProyectoDetalle>(`/proyectos/${proyectoId}/cerrar`, { method: 'POST', body: datos }),
    ['facturas'],
  );

export const useReabrirProyecto = () =>
  useMutacionDeProyecto((proyectoId: string) =>
    api<ProyectoDetalle>(`/proyectos/${proyectoId}/reabrir`, { method: 'POST' }),
  );

export const useRegenerarCronograma = () =>
  useMutacionDeProyecto(
    ({
      proyectoId,
      ...datos
    }: {
      proyectoId: string;
      modo?: string;
      duracionMeses?: number;
      condicionPago?: string;
    }) =>
      api<Cuota[]>(`/proyectos/${proyectoId}/cronograma/regenerar`, { method: 'POST', body: datos }),
    ['facturas'],
  );

export const useCrearCuotaManual = () =>
  useMutacionDeProyecto(
    ({
      proyectoId,
      ...datos
    }: {
      proyectoId: string;
    } & Omit<CrearCuotaManualInput, 'proyectoId'>) =>
      api<Cuota>(`/proyectos/${proyectoId}/cuotas`, { method: 'POST', body: datos }),
    ['facturas'],
  );

export const useActualizarCuotaManual = () =>
  useMutacionDeProyecto(
    ({
      cuotaId,
      ...datos
    }: {
      cuotaId: string;
    } & ActualizarCuotaManualInput) =>
      api<Cuota>(`/proyectos/cuotas/${cuotaId}`, { method: 'PATCH', body: datos }),
    ['facturas'],
  );

export const useEliminarCuotaManual = () =>
  useMutacionDeProyecto(
    (cuotaId: string) =>
      api<{ eliminada: boolean; mensaje: string }>(`/proyectos/cuotas/${cuotaId}`, {
        method: 'DELETE',
      }),
    ['facturas'],
  );

// ─────────────────────────────────────────────────────────────
// Facturación
//
// «Registrar» y no «emitir»: este sistema NO genera documentos tributarios.
// La factura electrónica se emite donde corresponda y aquí se anota para
// llevar el control de cobranza.
// ─────────────────────────────────────────────────────────────

export const useRegistrarFacturaDesdeCuota = () =>
  useMutacionDeProyecto(
    ({ cuotaId, ...datos }: { cuotaId: string; numero?: string; fechaEmision?: string }) =>
      api<Factura>(`/facturas/cuotas/${cuotaId}/emitir`, { method: 'POST', body: datos }),
    ['facturas'],
  );

/** Factura sin cuota previa, para cobros que no estaban en el cronograma. */
export const useRegistrarFacturaLibre = () =>
  useMutacionDeProyecto(
    (datos: Record<string, unknown>) => api<Factura>('/facturas', { method: 'POST', body: datos }),
    ['facturas'],
  );

export const useActualizarFactura = () =>
  useMutacionDeProyecto(
    ({ id, ...datos }: { id: string } & Record<string, unknown>) =>
      api<Factura>(`/facturas/${id}`, { method: 'PATCH', body: datos }),
    ['facturas'],
  );

export const useAnularFactura = () =>
  useMutacionDeProyecto(
    ({ id, motivo }: { id: string; motivo?: string }) =>
      api<Factura>(`/facturas/${id}/anular`, { method: 'POST', body: { motivo } }),
    ['facturas'],
  );

export const useRegistrarPagoEnProyecto = () =>
  useMutacionDeProyecto(
    ({
      facturaId,
      ...datos
    }: {
      facturaId: string;
      fecha: string;
      monto: number;
      medio: string;
      referencia?: string;
    }) => api<Factura>(`/facturas/${facturaId}/pagos`, { method: 'POST', body: datos }),
    ['facturas'],
  );

export const useEliminarPago = () =>
  useMutacionDeProyecto((pagoId: string) => api<Factura>(`/facturas/pagos/${pagoId}`, { method: 'DELETE' }), [
    'facturas',
  ]);

// ─────────────────────────────────────────────────────────────
// Proveedores
// ─────────────────────────────────────────────────────────────

export interface Proveedor {
  id: string;
  nombre: string;
  rut: string | null;
  contacto: string | null;
  _count?: { gastos: number };
}

export const useProveedores = () =>
  useQuery({
    queryKey: [...claves.catalogos, 'proveedores'],
    queryFn: () => api<Proveedor[]>('/proveedores'),
    staleTime: 10 * 60_000,
  });

export const useCrearProveedor = () =>
  useMutacionDeProyecto(
    (datos: Record<string, unknown>) => api<Proveedor>('/proveedores', { method: 'POST', body: datos }),
    ['catalogos'],
  );

// ─────────────────────────────────────────────────────────────
// Gastos recurrentes
//
// Los endpoints existían desde la fase 2 pero no tenían interfaz: hosting,
// sueldos y licencias son justamente los costos que se olvidan de cargar.
// ─────────────────────────────────────────────────────────────

export interface GastoRecurrente {
  id: string;
  descripcion: string;
  montoNeto: number;
  ivaPct: number;
  periodicidad: string;
  proximaFecha: string;
  fechaFin: string | null;
  activo: boolean;
  categoriaGasto: { id: string; nombre: string };
  proveedor: { id: string; nombre: string } | null;
  proveedorNombre?: string | null;
  proyecto: { id: string; codigo: string; nombre: string } | null;
  _count: { gastosGenerados: number };
}

export const useGastosRecurrentes = () =>
  useQuery({
    queryKey: [...claves.gastos, 'recurrentes'],
    queryFn: () => api<GastoRecurrente[]>('/gastos/recurrentes/listado'),
  });

export const useCrearGastoRecurrente = () =>
  useMutacionDeProyecto(
    (datos: Record<string, unknown>) =>
      api<GastoRecurrente>('/gastos/recurrentes', { method: 'POST', body: datos }),
    ['gastos'],
  );

export const useEliminarGastoRecurrente = () =>
  useMutacionDeProyecto((id: string) => api<void>(`/gastos/recurrentes/${id}`, { method: 'DELETE' }), ['gastos']);

export interface ResultadoGeneracion {
  generados: number;
  detalle: { descripcion: string; fecha: string; montoNeto: number }[];
  mensaje: string;
}

/** Materializa los gastos recurrentes ya vencidos y devuelve el detalle de lo creado. */
export const useGenerarRecurrentes = () =>
  // El tipo de variables se declara explícito porque esta mutación no recibe
  // ninguna: sin el `void`, TypeScript infiere `unknown` y exige un argumento.
  useMutacionDeProyecto<void, ResultadoGeneracion>(
    () => api<ResultadoGeneracion>('/gastos/recurrentes/generar', { method: 'POST' }),
    ['gastos'],
  );

// ─────────────────────────────────────────────────────────────
// Motor de predicción
// ─────────────────────────────────────────────────────────────

/**
 * Ajustar los parámetros cambia lo que el motor recomienda de aquí en adelante,
 * así que se invalida toda la rama de predicción: cualquier recomendación en
 * pantalla quedó obsoleta.
 */
function useMutacionDelMotor<TVariables, TResultado>(
  ejecutar: (variables: TVariables) => Promise<TResultado>,
) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ejecutar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.prediccion });
    },
  });
}

export const useActualizarParametros = () =>
  useMutacionDelMotor((datos: Partial<ParametrosPredictor>) =>
    api<ParametrosPredictor>('/prediccion/parametros', { method: 'POST', body: datos }),
  );

export const useRestablecerParametros = () =>
  useMutacionDelMotor<void, ParametrosPredictor>(() =>
    api<ParametrosPredictor>('/prediccion/parametros/restablecer', { method: 'POST' }),
  );

export const useAplicarEstrategiaMotor = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: { modo: ModoEstrategiaPredictor }) =>
      api<{ modo: string; nombre: string; mensaje: string }>('/prediccion/estrategia', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.prediccion });
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
    },
  });
};

export const useSimularPlayground = () =>
  useMutation({
    mutationFn: (datos: SimuladorPlaygroundInput) =>
      api<SimulacionPlaygroundRespuesta>('/prediccion/simular', {
        method: 'POST',
        body: datos,
      }),
  });

export const useTestImpacto = () =>
  useMutation({
    mutationFn: (datos: TestImpactoInput) =>
      api<TestImpactoRespuesta>('/prediccion/test-impacto', {
        method: 'POST',
        body: datos,
      }),
  });

/**
 * El backtesting no se cachea: es una consulta cara que el usuario dispara a
 * propósito para ver cómo rinde una configuración, y quiere el resultado del
 * momento, no uno guardado.
 */
export const useEjecutarBacktest = () =>
  useMutation({
    mutationFn: (datos: {
      minimoObservaciones?: number;
      configuracionAlternativa?: Partial<ParametrosPredictor>;
    }) => api<ResultadoBacktest | ComparacionBacktest>('/prediccion/backtest', { method: 'POST', body: datos }),
  });

// ─────────────────────────────────────────────────────────────
// Mutaciones de Configuración de la Empresa y Sistema
// ─────────────────────────────────────────────────────────────

export const useActualizarConfiguracionEmpresa = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: ActualizarConfiguracionInput) =>
      api<ConfiguracionEmpresaDto>('/configuracion', { method: 'PATCH', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
    },
  });
};

export const useCalcularMontosTributarios = () =>
  useMutation({
    mutationFn: (datos: CalcularMontosInput) =>
      api<DesgloseMontosDto>('/configuracion/calcular', { method: 'POST', body: datos }),
  });

export const useCrearCategoriaComercial = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearCategoriaInput) =>
      api<Categoria>('/configuracion/categorias', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.catalogos });
    },
  });
};

export const useActualizarCategoriaComercial = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: ActualizarCategoriaInput }) =>
      api<Categoria>(`/configuracion/categorias/${id}`, { method: 'PATCH', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.catalogos });
    },
  });
};

export const useEliminarCategoriaComercial = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ mensaje: string }>(`/configuracion/categorias/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.catalogos });
    },
  });
};

export const useCrearCategoriaGastoConfig = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearCategoriaGastoInput) =>
      api<{ id: string; nombre: string; tipo: string }>('/configuracion/categorias-gasto', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.catalogos });
    },
  });
};

export const useActualizarCategoriaGastoConfig = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: ActualizarCategoriaGastoInput }) =>
      api<{ id: string; nombre: string; tipo: string }>(`/configuracion/categorias-gasto/${id}`, {
        method: 'PATCH',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.catalogos });
    },
  });
};

export const useEliminarCategoriaGastoConfig = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ mensaje: string }>(`/configuracion/categorias-gasto/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.catalogos });
    },
  });
};

export const useActualizarParametrosPredictorConfig = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: ActualizarParametrosPredictorConfigInput) =>
      api<ParametrosPredictorDto>('/configuracion/predictor', { method: 'PATCH', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.prediccion });
    },
  });
};

export const useRestablecerParametrosPredictorConfig = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<ParametrosPredictorDto>('/configuracion/predictor/restablecer', { method: 'POST' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
      void cliente.invalidateQueries({ queryKey: claves.prediccion });
    },
  });
};

export const useCrearUsuarioEmpresa = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearUsuarioEmpresaInput) =>
      api<UsuarioEmpresaDto>('/configuracion/usuarios', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
    },
  });
};

export const useActualizarUsuarioEmpresa = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: ActualizarUsuarioEmpresaInput }) =>
      api<UsuarioEmpresaDto>(`/configuracion/usuarios/${id}`, { method: 'PATCH', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
    },
  });
};

export const useEliminarUsuarioEmpresa = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ mensaje: string }>(`/configuracion/usuarios/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.configuracion });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Organismos y Contrapartes
// ─────────────────────────────────────────────────────────────

export const useCrearOrganismo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearOrganismoInput) =>
      api<Organismo>('/organismos', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

export const useActualizarOrganismo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: ActualizarOrganismoInput }) =>
      api<Organismo>(`/organismos/${id}`, { method: 'PATCH', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

export const useEliminarOrganismo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/organismos/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

export const useCrearContactoOrganismo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ organismoId, datos }: { organismoId: string; datos: Omit<CrearContactoInput, 'organismoId'> }) =>
      api<ContactoOrganismo>(`/organismos/${organismoId}/contactos`, { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

export const useActualizarContactoOrganismo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: ActualizarContactoInput }) =>
      api<ContactoOrganismo>(`/organismos/contactos/${id}`, { method: 'PATCH', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

export const useEliminarContactoOrganismo = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/organismos/contactos/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Competencia & Inteligencia de Mercado (Módulo 3)
// ─────────────────────────────────────────────────────────────

export const useRegistrarActaCompetencia = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: RegistrarActaLicitacionInput) =>
      api<unknown>('/competencia/acta', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.competencia });
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useFusionarCompetidores = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: FusionarCompetidoresInput) =>
      api<{ exito: boolean; competidorDestinoId: string; mensaje: string }>('/competencia/fusionar', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.competencia });
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
    },
  });
};

export const useCrearCompetidor = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearCompetidorInput) =>
      api<unknown>('/competencia/competidores', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.competencia });
    },
  });
};

export const useActualizarCompetidor = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: ActualizarCompetidorInput }) =>
      api<unknown>(`/competencia/competidores/${id}`, {
        method: 'PATCH',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.competencia });
    },
  });
};

// ─────────────────────────────────────────────────────────────
// Pipeline & Gestión de Licitaciones (Módulo 4)
// ─────────────────────────────────────────────────────────────

export const useCrearLicitacionRapida = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearLicitacionInput) =>
      api<unknown>('/licitaciones', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
      void cliente.invalidateQueries({ queryKey: claves.organismos });
    },
  });
};

export const useCambiarEstadoLicitacion = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: CambiarEstadoLicitacionInput }) =>
      api<unknown>(`/licitaciones/${id}/estado`, {
        method: 'PATCH',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
      void cliente.invalidateQueries({ queryKey: claves.competencia });
    },
  });
};

export const useAdjudicarLicitacionDirecta = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      montoAdjudicadoNeto,
      montoAdjudicadoBruto,
      modalidadMonto,
    }: {
      id: string;
      montoAdjudicadoNeto?: number;
      montoAdjudicadoBruto?: number;
      modalidadMonto?: ModalidadMonto;
    }) =>
      api<unknown>(`/licitaciones/${id}/adjudicar`, {
        method: 'POST',
        body: { montoAdjudicadoNeto, montoAdjudicadoBruto, modalidadMonto },
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useActualizarLicitacion = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: Partial<CrearLicitacionInput> }) =>
      api<unknown>(`/licitaciones/${id}`, {
        method: 'PATCH',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useEliminarLicitacion = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/licitaciones/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
    },
  });
};

export const useRegistrarPagoRapidoFactura = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: RegistrarPagoRapidoInput }) =>
      api<{
        mensaje: string;
        pagoId: string;
        montoPagado: number;
        saldoRestante: number;
        situacionCobro: string;
      }>(`/facturas/${id}/pagar-rapido`, {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.facturas });
      void cliente.invalidateQueries({ queryKey: claves.proyectos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useCrearRemuneracion = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CrearRemuneracionInput) =>
      api<RemuneracionItem>('/rrhh/remuneraciones', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.rrhh });
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useCargarNominaMes = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: CargarNominaMesInput) =>
      api<{ mensaje: string; periodo: string; registrosCreados: number }>('/rrhh/remuneraciones/mes', {
        method: 'POST',
        body: datos,
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.rrhh });
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useMarcarRemuneracionPagada = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, datos }: { id: string; datos?: PagarRemuneracionInput }) =>
      api<RemuneracionItem>(`/rrhh/remuneraciones/${id}/pagar`, {
        method: 'PATCH',
        body: datos ?? {},
      }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.rrhh });
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

export const useAsignarBonoLicitacion = () => {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (datos: AsignarBonoLicitacionInput) =>
      api<RemuneracionItem>('/rrhh/bono-licitacion', { method: 'POST', body: datos }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: claves.rrhh });
      void cliente.invalidateQueries({ queryKey: claves.gastos });
      void cliente.invalidateQueries({ queryKey: claves.licitaciones });
      void cliente.invalidateQueries({ queryKey: claves.analitica });
    },
  });
};

